import { SavingsConfig, SavingsValuesConfig, NormalizedProductType, ProductType } from '../types';
import { normalizeProductType, formatCurrency } from '../utils/helpers';

/**
 * Etichette dei benefici per tipo di prodotto - Partnership Email Configuration
 */
export const SAVINGS_LABELS: SavingsConfig = {
  base: {
    Photovoltaic: {
      install_timeline: '+3.825€ tramite PNRR CER Enpal',
      name_form: '+3.430€ di Rivendita Energia Annuale',
      email_form: '+759€ di Surplus su rivendita di energia tramite CER',
      phone_form: '+23.800€ di Risparmio medio in Bolletta',
    },
    Heatpump: {
      install_timeline: '+3.553€ tramite Conto Termico 3.0',
      name_form: '+7.160€ tramite Ecobonus',
      email_form: '+23.800€ di Risparmio medio in Bolletta',
      phone_form: '+34.513€ di Risparmio Totale',
    },
    Photovoltaic_Heatpump: {
      install_timeline: '+3.825€ tramite PNRR CER Enpal',
      name_form: '+3.553€ tramite Conto Termico 3.0',
      email_form: '+4.189€ di Rivendita con Surplus CER',
      phone_form: '+23.800€ di Risparmio medio in Bolletta',
    },
  },
  overrideWhenFalse: {
    Photovoltaic: {
      install_timeline: '+9.899€ tramite Ecobonus',
      name_form: '+3.430€ di Rivendita Energia Annuale',
      email_form: '+759€ di Surplus su rivendita di energia tramite CER',
      phone_form: '+23.800€ di Risparmio medio in Bolletta',
    },
    Heatpump: {
      install_timeline: '+3.553€ tramite Conto Termico 3.0',
      name_form: '+7.160€ tramite Ecobonus',
      email_form: '+23.800€ di Risparmio medio in Bolletta',
      phone_form: '+34.513€ di Risparmio totale',
    },
    Photovoltaic_Heatpump: {
      install_timeline: '+9.899€ tramite Ecobonus',
      name_form: '+3.553€ tramite Conto Termico 3.0',
      email_form: '+4.189€ di Rivendita con Surplus CER',
      phone_form: '+23.800€ di Risparmio medio in Bolletta',
    },
  },
};

/**
 * Valori numerici dei benefici per tipo di prodotto
 */
export const SAVINGS_VALUES: SavingsValuesConfig = {
  base: {
    Photovoltaic: {
      install_timeline: 3825,
      name_form: 3430,
      email_form: 759,
      phone_form: 23800,
    },
    Heatpump: {
      install_timeline: 3553,
      name_form: 7160,
      email_form: 23800,
      phone_form: 0,
    },
    Photovoltaic_Heatpump: {
      install_timeline: 3825,
      name_form: 3553,
      email_form: 4189,
      phone_form: 23800,
    },
  },
  overrideWhenFalse: {
    Photovoltaic: {
      install_timeline: 9899,
      name_form: 3430,
      email_form: 759,
      phone_form: 23800,
    },
    Heatpump: {
      install_timeline: 3553,
      name_form: 7160,
      email_form: 23800,
      phone_form: 0,
    },
    Photovoltaic_Heatpump: {
      install_timeline: 9899,
      name_form: 3553,
      email_form: 4189,
      phone_form: 23800,
    },
  },
};

/**
 * Tooltip text per tipo di prodotto
 */
export const TOOLTIP_TEXTS: Record<NormalizedProductType, string> = {
  Photovoltaic:
    'calcolato sulla base del nostro impianto best-seller: Impianto Fotovoltaico da 9kW + 16kW di Batteria',
  Heatpump:
    'calcolato sulla base del nostro impianto best-seller: Pompa di calore 12kW full-electric',
  Photovoltaic_Heatpump:
    'calcolato sulla base del nostro impianto best-seller: Impianto Fotovoltaico da 9kW + 16kW di Batteria & Pompa di calore 12kW full-electric',
};

/**
 * Ottiene il testo del tooltip in base al prodotto
 */
export function getTooltipText(productType?: ProductType): string {
  const normalized = normalizeProductType(productType);
  return TOOLTIP_TEXTS[normalized] || TOOLTIP_TEXTS.Photovoltaic;
}

/**
 * Ottiene il testo del beneficio per una slide specifica
 */
export function getSavingsText(
  slideId: string,
  productType?: ProductType,
  zipFlag?: boolean | null
): string | null {
  const normalized = normalizeProductType(productType);
  const key = slideId as keyof typeof SAVINGS_LABELS.base.Photovoltaic;

  if (zipFlag === false) {
    const overrideLabels = SAVINGS_LABELS.overrideWhenFalse[normalized];
    return overrideLabels?.[key] || null;
  }

  const baseLabels = SAVINGS_LABELS.base[normalized];
  return baseLabels?.[key] || null;
}

/**
 * Calcola il risparmio totale cumulativo fino a una slide
 */
export function calculateTotalSavings(
  slideId: string,
  productType?: ProductType,
  zipFlag?: boolean | null
): string {
  const normalized = normalizeProductType(productType);
  const slideOrder = ['install_timeline', 'name_form', 'email_form', 'phone_form'];
  const currentIndex = slideOrder.indexOf(slideId);

  if (currentIndex === -1) return '';

  const values =
    zipFlag === false
      ? SAVINGS_VALUES.overrideWhenFalse[normalized]
      : SAVINGS_VALUES.base[normalized];

  let total = 0;
  for (let i = 0; i <= currentIndex; i++) {
    const key = slideOrder[i] as keyof typeof values;
    total += values[key] || 0;
  }

  return formatCurrency(total);
}

/**
 * Genera i dati per il riepilogo finale dei risparmi
 */
export interface SavingsSummaryItem {
  label: string;
  value: string;
  isCo2?: boolean;
  category?: 'PV' | 'HP';
}

export function generateSavingsSummary(
  productType?: ProductType,
  zipFlag?: boolean | null
): {
  items: SavingsSummaryItem[];
  total: number;
  co2Saved: string;
} {
  const normalized = normalizeProductType(productType);
  const labels =
    zipFlag === false
      ? SAVINGS_LABELS.overrideWhenFalse[normalized]
      : SAVINGS_LABELS.base[normalized];
  const values =
    zipFlag === false
      ? SAVINGS_VALUES.overrideWhenFalse[normalized]
      : SAVINGS_VALUES.base[normalized];

  const slideOrder = ['install_timeline', 'name_form', 'email_form', 'phone_form'];
  const items: SavingsSummaryItem[] = [];
  let total = 0;
  let co2Saved = 'Scoprilo in chiamata';

  slideOrder.forEach((slideId) => {
    const key = slideId as keyof typeof labels;
    const labelText = labels[key];
    const value = values[key] || 0;

    if (!labelText) return;

    // Skip phone_form for Heatpump (shows total)
    if (normalized === 'Heatpump' && slideId === 'phone_form') return;

    // Check for CO2 entry
    if (labelText.toLowerCase().includes('co2')) {
      const match = labelText.match(/([+\-][\d.,]+kg)/);
      co2Saved = match ? match[0] : co2Saved;
      return;
    }

    // Determine label based on content
    let displayLabel = 'Beneficio';
    if (labelText.toLowerCase().includes('bolletta')) {
      displayLabel = 'Risparmio medio in Bolletta';
    } else if (labelText.toLowerCase().includes('surplus')) {
      displayLabel = 'Surplus rivendita energia CER';
    } else if (labelText.toLowerCase().includes('rivendita') && labelText.toLowerCase().includes('surplus')) {
      displayLabel = 'Rivendita con Surplus CER';
    } else if (labelText.toLowerCase().includes('rivendita')) {
      displayLabel = 'Rivendita Energia';
    } else if (labelText.toLowerCase().includes('ecobonus')) {
      displayLabel = 'Ecobonus';
    } else if (labelText.toLowerCase().includes('pnrr') || labelText.toLowerCase().includes('cer enpal')) {
      displayLabel = 'PNRR CER Enpal';
    } else if (labelText.toLowerCase().includes('cashback')) {
      displayLabel = 'Cashback PNRR';
    } else if (labelText.toLowerCase().includes('conto termico')) {
      displayLabel = 'Conto Termico 3.0';
    }

    if (value > 0) {
      items.push({
        label: displayLabel,
        value: formatCurrency(value),
      });
      total += value;
    }
  });

  // Add default "Risparmio medio in bolletta" if not already present
  const hasBolletta = items.some((item) =>
    item.label.toLowerCase().includes('bolletta')
  );
  if (!hasBolletta) {
    items.push({
      label: 'Risparmio medio in bolletta',
      value: formatCurrency(23800),
    });
    total += 23800;
  }

  return { items, total, co2Saved };
}


